import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { ArrowRight, Check } from "lucide-react";
import { Button } from "../ui/Button";
import { Link } from "react-router-dom";

const Section = styled.section`
  position: relative;
  padding: 96px 16px;
  overflow: hidden;

  @media (min-width: ${({ theme }) => theme.breakpoints.sm}px) {
    padding: 96px 24px;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.lg}px) {
    padding: 120px 32px;
  }
`;

const BackgroundBlob = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 1000px;
  height: 1000px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.gradients.primary}20;
  filter: blur(128px);
  animation: pulse-slow 6s ease-in-out infinite;
  pointer-events: none;
`;

const Container = styled.div`
  max-width: 896px;
  margin: 0 auto;
  position: relative;
  z-index: 10;
`;

const Card = styled.div`
  padding: 64px 32px;
  border-radius: 32px;
  background: ${({ theme }) => theme.colors.light?.card?.background || theme.colors.light?.background || "white"};
  border: 1px solid ${({ theme }) => theme.colors.light?.border || "hsl(240, 10%, 90%)"};
  box-shadow: ${({ theme }) => theme.shadows.lg};
  text-align: center;

  body.dark & {
    background: ${({ theme }) => theme.colors.dark?.card?.background || theme.colors.dark?.background || "hsl(240, 15%, 10%)"};
    border-color: ${({ theme }) => theme.colors.dark?.border || "hsl(240, 15%, 18%)"};
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.lg}px) {
    padding: 80px 64px;
  }
`;

const Title = styled.h2`
  font-family: ${({ theme }) => theme.typography.fontFamily.display};
  font-size: ${({ theme }) => theme.typography.fontSize.h2};
  font-weight: 700;
  line-height: 1.1;
  margin: 0 0 16px 0;
  color: ${({ theme }) => theme.colors.light?.foreground || theme.colors.text.heading};

  body.dark & {
    color: ${({ theme }) => theme.colors.dark?.foreground || "#ffffff"};
  }
`;

const GradientTitle = styled.span`
  background: ${({ theme }) => theme.colors.gradients.primary};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const Description = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.bodyLarge};
  line-height: 1.6;
  color: ${({ theme }) => theme.colors.light?.muted?.foreground || theme.colors.text.muted};
  margin: 0 0 32px 0;

  body.dark & {
    color: ${({ theme }) => theme.colors.dark?.muted?.foreground || "hsl(240, 10%, 60%)"};
  }
`;

const BenefitsList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0 0 40px 0;
  display: flex;
  flex-direction: column;
  gap: 16px;
  align-items: flex-start;
  max-width: 400px;
  margin-left: auto;
  margin-right: auto;
`;

const BenefitItem = styled.li`
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: ${({ theme }) => theme.typography.fontSize.body};
  color: ${({ theme }) => theme.colors.light?.foreground || theme.colors.text.heading};

  body.dark & {
    color: ${({ theme }) => theme.colors.dark?.foreground || "#ffffff"};
  }
`;

const CheckIcon = styled(Check)`
  width: 20px;
  height: 20px;
  color: ${({ theme }) => theme.colors.light?.primary || theme.colors.primary.light};
  flex-shrink: 0;

  body.dark & {
    color: ${({ theme }) => theme.colors.dark?.primary || theme.colors.primary.light};
  }
`;

const CTAWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  align-items: center;

  @media (min-width: ${({ theme }) => theme.breakpoints.sm}px) {
    flex-direction: row;
    justify-content: center;
  }
`;

const benefitKeys = ["landing.cta.benefit1", "landing.cta.benefit2", "landing.cta.benefit3", "landing.cta.benefit4"];

export function CTASection() {
  const { t } = useTranslation();
  return (
    <Section>
      <BackgroundBlob />
      <Container>
        <Card>
          <Title>
            {t("landing.cta.title")} <GradientTitle>{t("landing.cta.titleHighlight")}</GradientTitle>{t("landing.cta.titleSuffix")}
          </Title>
          <Description>{t("landing.cta.description")}</Description>

          <BenefitsList>
            {benefitKeys.map((key) => (
              <BenefitItem key={key}>
                <CheckIcon />
                <span>{t(key)}</span>
              </BenefitItem>
            ))}
          </BenefitsList>

          <CTAWrapper>
            <Link to="/signup" style={{ textDecoration: "none" }}>
              <Button variant="hero" size="xl">
                {t("landing.cta.startFreeTrial")}
                <ArrowRight size={20} />
              </Button>
            </Link>
            <Link to="/pricing" style={{ textDecoration: "none" }}>
              <Button variant="hero-outline" size="xl">
                {t("landing.cta.viewPricing")}
              </Button>
            </Link>
          </CTAWrapper>
        </Card>
      </Container>
    </Section>
  );
}
